
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { AlertTriangle, Zap, Brain, User, MapPin, TrendingUp } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { ClimateFacility, ClimateRisk } from '@/types/climate';
import { useToast } from '@/hooks/use-toast';

export const RiskDetection = () => {
  const [facilities, setFacilities] = useState<ClimateFacility[]>([]);
  const [risks, setRisks] = useState<ClimateRisk[]>([]);
  const [loading, setLoading] = useState(true);
  const [detecting, setDetecting] = useState(false);
  const [selectedFacility, setSelectedFacility] = useState<string>('all');
  const [detectionMethod, setDetectionMethod] = useState<'automated' | 'ai_assisted' | 'manual'>('automated');
  const { toast } = useToast();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [facilitiesRes, risksRes] = await Promise.all([
        supabase.from('climate_facilities' as any).select('*'),
        supabase.from('climate_risks' as any).select('*').order('created_at', { ascending: false })
      ]);

      setFacilities((facilitiesRes.data as unknown as ClimateFacility[]) || []);
      setRisks((risksRes.data as unknown as ClimateRisk[]) || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const runRiskDetection = async () => {
    if (facilities.length === 0) {
      toast({
        title: "No Facilities",
        description: "Please add facilities before running risk detection",
        variant: "destructive",
      });
      return;
    }

    setDetecting(true);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');

      const targetFacilities = selectedFacility === 'all' 
        ? facilities 
        : facilities.filter(f => f.id === selectedFacility);

      // Simulate risk detection process
      for (const facility of targetFacilities) {
        const detectedRisks = await detectRisksForFacility(facility, detectionMethod, user.id);
        
        if (detectedRisks.length > 0) {
          const { error } = await supabase
            .from('climate_risks' as any)
            .insert(detectedRisks);
          
          if (error) throw error;
        }
      }

      toast({
        title: "Success",
        description: `Risk detection completed for ${targetFacilities.length} facility(ies)`,
      });
      
      loadData();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to run risk detection",
        variant: "destructive",
      });
    } finally {
      setDetecting(false);
    }
  };

  const detectRisksForFacility = async (facility: ClimateFacility, method: string, userId: string): Promise<Partial<ClimateRisk>[]> => {
    const risks: Partial<ClimateRisk>[] = [];
    
    // Simulate automated risk detection based on facility characteristics
    if (method === 'automated' || method === 'ai_assisted') {
      // Location-based risks
      if (facility.country) {
        if (['USA', 'Australia', 'Spain'].includes(facility.country)) {
          risks.push({
            user_id: userId,
            facility_id: facility.id,
            risk_name: 'Wildfire Risk',
            risk_type: 'physical_acute',
            risk_category: 'wildfire',
            risk_description: `Wildfire risk detected for ${facility.facility_name} based on location`,
            detection_method: method,
            probability_score: Math.random() * 40 + 30, // 30-70
            impact_score: Math.random() * 30 + 40, // 40-70
            time_horizon: 'medium_term',
            confidence_level: method === 'ai_assisted' ? 'high' : 'medium',
            data_sources: ['Location Analysis', 'Historical Data'],
            risk_status: 'identified',
            financial_impact_low: Math.random() * 50000 + 10000,
            financial_impact_high: Math.random() * 200000 + 100000
          });
        }
        
        if (['Bangladesh', 'Netherlands', 'Florida'].includes(facility.country || facility.region || '')) {
          risks.push({
            user_id: userId,
            facility_id: facility.id,
            risk_name: 'Flooding Risk',
            risk_type: 'physical_acute',
            risk_category: 'flooding',
            risk_description: `Flood risk identified for ${facility.facility_name} due to coastal/low-lying location`,
            detection_method: method,
            probability_score: Math.random() * 50 + 25,
            impact_score: Math.random() * 40 + 50,
            time_horizon: 'short_term',
            confidence_level: 'high',
            data_sources: ['Elevation Data', 'Climate Models'],
            risk_status: 'identified',
            financial_impact_low: Math.random() * 100000 + 50000,
            financial_impact_high: Math.random() * 500000 + 200000
          });
        }
      }

      // Facility type-based risks
      if (facility.facility_type === 'manufacturing') {
        risks.push({
          user_id: userId,
          facility_id: facility.id,
          risk_name: 'Carbon Pricing Impact',
          risk_type: 'transition_policy',
          risk_category: 'carbon_pricing',
          risk_description: `Manufacturing facility ${facility.facility_name} exposed to carbon pricing regulations`,
          detection_method: method,
          probability_score: Math.random() * 30 + 60, // 60-90
          impact_score: Math.random() * 40 + 30,
          time_horizon: 'medium_term',
          confidence_level: 'high',
          data_sources: ['Regulatory Analysis', 'Industry Reports'],
          risk_status: 'identified',
          financial_impact_low: Math.random() * 75000 + 25000,
          financial_impact_high: Math.random() * 300000 + 100000
        });
      }

      if (facility.facility_type === 'data_center') {
        risks.push({
          user_id: userId,
          facility_id: facility.id,
          risk_name: 'Extreme Heat Risk',
          risk_type: 'physical_chronic',
          risk_category: 'extreme_heat',
          risk_description: `Data center ${facility.facility_name} vulnerable to extreme heat affecting cooling systems`,
          detection_method: method,
          probability_score: Math.random() * 40 + 40,
          impact_score: Math.random() * 50 + 40,
          time_horizon: 'long_term',
          confidence_level: 'medium',
          data_sources: ['Temperature Projections', 'Infrastructure Analysis'],
          risk_status: 'identified',
          financial_impact_low: Math.random() * 80000 + 20000,
          financial_impact_high: Math.random() * 400000 + 150000
        });
      }
    }

    return risks;
  };

  const getRiskSeverityColor = (score?: number) => {
    if (!score) return 'secondary';
    if (score >= 70) return 'destructive';
    if (score >= 40) return 'default';
    return 'secondary';
  };

  const filteredRisks = selectedFacility === 'all' 
    ? risks 
    : risks.filter(r => r.facility_id === selectedFacility);

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading risk detection...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Automated Risk Detection</h2>
        <p className="text-muted-foreground">
          Identify climate risks using automated analysis, AI assistance, or manual assessment
        </p>
      </div>

      {/* Detection Controls */}
      <Card>
        <CardHeader>
          <CardTitle>Risk Detection Configuration</CardTitle>
          <CardDescription>Configure and run automated risk detection</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Target Facility</label>
              <Select value={selectedFacility} onValueChange={setSelectedFacility}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Facilities</SelectItem>
                  {facilities.map((facility) => (
                    <SelectItem key={facility.id} value={facility.id}>
                      {facility.facility_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Detection Method</label>
              <Select value={detectionMethod} onValueChange={(value: any) => setDetectionMethod(value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="automated">
                    <div className="flex items-center gap-2">
                      <Zap className="h-4 w-4" />
                      Automated
                    </div>
                  </SelectItem>
                  <SelectItem value="ai_assisted">
                    <div className="flex items-center gap-2">
                      <Brain className="h-4 w-4" />
                      AI-Assisted
                    </div>
                  </SelectItem>
                  <SelectItem value="manual">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      Manual Survey
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-end">
              <Button 
                onClick={runRiskDetection} 
                disabled={detecting || facilities.length === 0}
                className="w-full"
              >
                {detecting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Detecting...
                  </>
                ) : (
                  <>
                    <AlertTriangle className="h-4 w-4 mr-2" />
                    Run Detection
                  </>
                )}
              </Button>
            </div>
          </div>

          {detecting && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Analyzing facilities...</span>
                <span>Processing</span>
              </div>
              <Progress value={33} className="w-full" />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detection Methods Info */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-blue-500" />
              Automated Detection
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Uses location data, facility type, and historical climate data to identify potential risks automatically.
            </p>
            <ul className="text-xs text-muted-foreground mt-2 space-y-1">
              <li>• Location-based hazard mapping</li>
              <li>• Facility vulnerability assessment</li>
              <li>• Historical event analysis</li>
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Brain className="h-5 w-5 text-purple-500" />
              AI-Assisted Detection
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Combines automated analysis with AI models for enhanced risk identification and scoring.
            </p>
            <ul className="text-xs text-muted-foreground mt-2 space-y-1">
              <li>• Machine learning risk models</li>
              <li>• Pattern recognition</li>
              <li>• Enhanced confidence scoring</li>
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5 text-green-500" />
              Manual Survey
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Expert-guided risk assessment through structured questionnaires and manual analysis.
            </p>
            <ul className="text-xs text-muted-foreground mt-2 space-y-1">
              <li>• Expert knowledge integration</li>
              <li>• Customized risk assessment</li>
              <li>• Stakeholder input collection</li>
            </ul>
          </CardContent>
        </Card>
      </div>

      {/* Detected Risks */}
      <Card>
        <CardHeader>
          <CardTitle>Detected Climate Risks</CardTitle>
          <CardDescription>
            {filteredRisks.length} risk(s) identified
          </CardDescription>
        </CardHeader>
        <CardContent>
          {filteredRisks.length === 0 ? (
            <div className="text-center py-8">
              <AlertTriangle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Risks Detected</h3>
              <p className="text-muted-foreground">
                Run risk detection to identify potential climate risks for your facilities
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredRisks.map((risk) => (
                <Card key={risk.id}>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-lg">{risk.risk_name}</CardTitle>
                        <CardDescription className="flex items-center gap-2 mt-1">
                          <MapPin className="h-4 w-4" />
                          {facilities.find(f => f.id === risk.facility_id)?.facility_name || 'Unknown Facility'}
                        </CardDescription>
                      </div>
                      <div className="flex gap-2">
                        <Badge variant={getRiskSeverityColor(risk.risk_score)}>
                          Risk Score: {risk.risk_score?.toFixed(0) || 0}
                        </Badge>
                        <Badge variant="outline">
                          {risk.detection_method?.replace('_', ' ')}
                        </Badge>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div>
                        <p className="text-sm font-medium">Risk Type</p>
                        <p className="text-sm text-muted-foreground">
                          {risk.risk_type?.replace('_', ' ').toUpperCase()}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium">Category</p>
                        <p className="text-sm text-muted-foreground">
                          {risk.risk_category?.replace('_', ' ').toUpperCase()}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium">Time Horizon</p>
                        <p className="text-sm text-muted-foreground">
                          {risk.time_horizon?.replace('_', ' ').toUpperCase()}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium">Confidence</p>
                        <Badge variant="secondary" className="text-xs">
                          {risk.confidence_level?.toUpperCase()}
                        </Badge>
                      </div>
                    </div>

                    {risk.risk_description && (
                      <div className="mb-4">
                        <p className="text-sm font-medium">Description</p>
                        <p className="text-sm text-muted-foreground">{risk.risk_description}</p>
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm font-medium">Probability Score</p>
                        <div className="flex items-center gap-2">
                          <Progress value={risk.probability_score || 0} className="flex-1" />
                          <span className="text-sm text-muted-foreground">
                            {risk.probability_score?.toFixed(0) || 0}%
                          </span>
                        </div>
                      </div>
                      <div>
                        <p className="text-sm font-medium">Impact Score</p>
                        <div className="flex items-center gap-2">
                          <Progress value={risk.impact_score || 0} className="flex-1" />
                          <span className="text-sm text-muted-foreground">
                            {risk.impact_score?.toFixed(0) || 0}%
                          </span>
                        </div>
                      </div>
                    </div>

                    {(risk.financial_impact_low || risk.financial_impact_high) && (
                      <div className="mt-4">
                        <p className="text-sm font-medium">Financial Impact Range</p>
                        <p className="text-sm text-muted-foreground">
                          ${risk.financial_impact_low?.toLocaleString() || 0} - ${risk.financial_impact_high?.toLocaleString() || 0}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
